import {
    createHandlerChainRunner,
    Handler,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { sendRemoveFromCartEvent } from "@insite/client-framework/OdpEvents/UpdateCartEvents";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import {
    RemoveCartLineApiParameter,
    removeCartLineWithResult as removeCartLineApi,
} from "@insite/client-framework/Services/CartService";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";

type Parameter = {
    cartLineId: string;
    onSuccess?: () => void;
};

type Props = {
    apiParameter: RemoveCartLineApiParameter;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginRemoveCartLine: HandlerType = props => {
    props.dispatch({
        type: "Context/BeginUpdatingCart",
    });
    props.dispatch({
        type: "Pages/Cart/BeginRemoveCartLine",
        cartLineId: props.parameter.cartLineId,
    });
};

export const SendRemoveFromCartEvent: HandlerType = props => {
    const state = props.getState();

    const cartLines = getCurrentCartState(state).value?.cartLines;
    if (!cartLines) return;

    const { cartLineId } = props.parameter;

    const line = cartLines.find(({ id }) => id === cartLineId);

    if (!line) return;

    const { productId, qtyOrdered } = line;

    if (!productId || !qtyOrdered) return;

    sendRemoveFromCartEvent({ productId, qtyOrdered }, state);
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        cartLineId: props.parameter.cartLineId,
        cartId: API_URL_CURRENT_FRAGMENT,
    };
};

export const RemoveCartLine: HandlerType = async props => {
    const result = await removeCartLineApi(props.apiParameter);
    if (!result.successful) {
        if (result.statusCode === 403) {
            const { isAuthenticated, rememberMe } = props.getState().context.session;
            (isAuthenticated || rememberMe) && window.location.reload();
            return false;
        }

        props.dispatch({
            type: "Pages/Cart/CompleteRemoveCartLine",
            cartLineId: props.parameter.cartLineId,
        });
        props.dispatch({
            type: "Context/CompleteUpdatingCart",
        });
        return false;
    }
};

export const LoadCart: HandlerType = props => {
    props.dispatch(loadCurrentCart({ getPromotions: true }));
};

/** @deprecated Not needed anymore */
export const LoadPromotions: HandlerType = () => {};

export const DispatchCompleteRemoveCartLine: HandlerType = props => {
    props.dispatch({
        type: "Pages/Cart/CompleteRemoveCartLine",
        cartLineId: props.parameter.cartLineId,
    });
    props.dispatch({
        type: "Context/CompleteUpdatingCart",
    });
    props.dispatch({
        type: "Data/OrderApprovals/Reset",
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginRemoveCartLine,
    SendRemoveFromCartEvent,
    PopulateApiParameter,
    RemoveCartLine,
    LoadCart,
    LoadPromotions,
    DispatchCompleteRemoveCartLine,
    ExecuteOnSuccessCallback,
];

const removeCartLine = createHandlerChainRunner(chain, "RemoveCartLine");
export default removeCartLine;
